// Every place's track at a country's zoom: Ireland's railways, Germany's
// S-Bahns and regional lines, the American cities' rail, drawn thin from
// one prebuilt file (public/overview.json, scripts/build-overview.ts) before
// any city's own network is fetched and with no trains. A region's own
// network replaces its overview once it's drawn (setDrawn), so the two never
// show together.
//
// The file is fetched once, the first time the map is at OVERVIEW_ZOOM or
// closer: the world view itself still fetches nothing but the page and the
// basemap.

import type { FeatureCollection, LineString } from 'geojson';
import type { FilterSpecification, Map as MapLibreMap } from 'maplibre-gl';
import type { Box } from '@tol/core/onscreen';

export interface OverviewFile {
  readonly built: string;
  // Region id -> [color, Google-encoded polylines (precision 4, about 10 m)] per color.
  readonly regions: Readonly<Record<string, readonly (readonly [color: string, paths: readonly string[]])[]>>;
}

// MapLibre's zooms: a country (Germany fills a laptop at about 5.5) up to a
// region's own LOAD_ZOOM (viewport.ts), with a little overlap while its
// network loads.
export const OVERVIEW_ZOOM = 3;
const OVERVIEW_MAX_ZOOM = 9;
export const OVERVIEW_LAYER = 'world-overview';

export function isOverviewFile(v: unknown): v is OverviewFile {
  if (typeof v !== 'object' || v === null) return false;
  const regions = (v as { regions?: unknown }).regions;
  return typeof regions === 'object' && regions !== null;
}

// Google's polyline encoding at precision 4: a tenth of a pixel at zoom 9.
const SCALE = 1e4;
const enc = (n: number): string => {
  let v = n < 0 ? ~(n << 1) : n << 1;
  let s = '';
  while (v >= 0x20) {
    s += String.fromCodePoint((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  return s + String.fromCodePoint(v + 63);
};
export function encodeOverviewPath(pts: readonly (readonly [lat: number, lon: number])[]): string {
  let out = '';
  let pa = 0;
  let po = 0;
  for (const [la, lo] of pts) {
    const a = Math.round(la * SCALE);
    const b = Math.round(lo * SCALE);
    if (out !== '' && a === pa && b === po) continue;
    out += enc(a - pa) + enc(b - po);
    pa = a;
    po = b;
  }
  return out;
}
// [lon, lat], as GeoJSON has them.
export function decodeOverviewPath(str: string): [lon: number, lat: number][] {
  const pts: [number, number][] = [];
  let i = 0;
  let lat = 0;
  let lon = 0;
  const next = (): number => {
    let r = 0;
    let sh = 0;
    let b: number;
    do {
      b = (str.codePointAt(i++) ?? Number.NaN) - 63;
      r |= (b & 0x1f) << sh;
      sh += 5;
    } while (b >= 0x20 && i <= str.length);
    return r & 1 ? ~(r >> 1) : r >> 1;
  };
  while (i < str.length) {
    lat += next();
    lon += next();
    pts.push([lon / SCALE, lat / SCALE]);
  }
  return pts;
}

// The file as GeoJSON lines, [lon, lat], each with its region (r) and color (c).
export function overviewFeatures(file: OverviewFile): FeatureCollection<LineString, { r: string; c: string }> {
  const features: FeatureCollection<LineString, { r: string; c: string }>['features'] = [];
  for (const [r, colors] of Object.entries(file.regions)) {
    for (const [c, paths] of colors) {
      for (const path of paths) {
        const coordinates = decodeOverviewPath(path);
        if (coordinates.length > 1)
          features.push({ type: 'Feature', properties: { r, c }, geometry: { type: 'LineString', coordinates } });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

// Hide the regions whose own network is drawn.
export function overviewFilter(drawn: readonly string[]): FilterSpecification {
  return ['!', ['in', ['get', 'r'], ['literal', [...drawn]]]];
}

export interface Overview {
  // Called on every camera change: fetches the file the first time it's wanted.
  readonly update: (zoom: number) => void;
  // The regions whose own network is on the map.
  readonly setDrawn: (ids: readonly string[]) => void;
  // Regions and coverage of the latest detailed source submission. Preparation
  // alone cannot hand off to detail, even if the previous source is loaded.
  readonly setSubmitted: (ids: readonly string[], bounds: Box) => void;
}

export function createOverview(map: MapLibreMap, url = 'overview.json'): Overview {
  let asked = false;
  // After a failed fetch, not again for a while.
  let retryAt = 0;
  let drawn: readonly string[] = [];
  let wanted: readonly string[] = [];
  let submitted: readonly string[] = [];
  let submittedBounds: Box | null = null;
  const filter = (): void => {
    if (map.getLayer(OVERVIEW_LAYER)) map.setFilter(OVERVIEW_LAYER, overviewFilter(drawn));
  };
  // Initial detail must have usable tiles before replacing its overview.
  // Later zooms and setData refreshes retain those tiles: keep accepted
  // regions excluded so their coarse overview never flashes through.
  map.on('render', () => {
    // A rapid revisit can retain the region's old loaded source while its
    // replacement prepares. Restore the overview as soon as the camera
    // leaves that source's accepted crop, including when the same regions
    // remain wanted throughout an ordinary zoom.
    const bounds = map.getBounds();
    const covered =
      submittedBounds !== null &&
      bounds.getWest() >= submittedBounds[0] &&
      bounds.getSouth() >= submittedBounds[1] &&
      bounds.getEast() <= submittedBounds[2] &&
      bounds.getNorth() <= submittedBounds[3];
    if (!covered) {
      const retained = drawn.filter((id) => !wanted.includes(id));
      if (retained.length !== drawn.length) {
        drawn = retained;
        filter();
      }
      return;
    }
    if (!map.getSource('lines')) return;
    if (!map.isSourceLoaded('lines')) return;
    if (!wanted.every((id) => submitted.includes(id))) return;
    if (drawn === wanted) return;
    drawn = wanted;
    filter();
  });
  const add = (file: OverviewFile): void => {
    map.addSource(OVERVIEW_LAYER, { type: 'geojson', data: overviewFeatures(file), maxzoom: OVERVIEW_MAX_ZOOM });
    // Under the networks' own lines (and so under the basemap's labels).
    const before = map.getStyle().layers.find((l) => 'source' in l && l.source === 'lines')?.id;
    map.addLayer(
      {
        id: OVERVIEW_LAYER,
        type: 'line',
        source: OVERVIEW_LAYER,
        minzoom: OVERVIEW_ZOOM,
        filter: overviewFilter(drawn),
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'c'],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], OVERVIEW_ZOOM, 0, OVERVIEW_ZOOM + 0.5, 0.75],
          'line-width': ['interpolate', ['linear'], ['zoom'], OVERVIEW_ZOOM, 0.8, 6, 1.4, 8, 2],
        },
      },
      before,
    );
  };
  const fetchFile = async (): Promise<void> => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
    const v: unknown = await res.json();
    if (!isOverviewFile(v)) throw new TypeError('overview.json is not an overview');
    add(v);
  };
  return {
    update(zoom) {
      // Not where it isn't drawn: a city's own view (a page opened at /nyc)
      // was fetching it alongside the city's network.
      if (asked || zoom < OVERVIEW_ZOOM - 0.5 || zoom >= OVERVIEW_MAX_ZOOM || Date.now() < retryAt) return;
      asked = true;
      void fetchFile().catch((error: unknown) => {
        // Ask again on a move half a minute on.
        asked = false;
        retryAt = Date.now() + 30_000;
        console.warn('overview unavailable', error);
      });
    },
    setDrawn(ids) {
      if (ids.length === wanted.length && ids.every((id, i) => wanted[i] === id)) return;
      wanted = [...ids];
      // A region leaving the detailed source needs its overview immediately.
      drawn = drawn.filter((id) => wanted.includes(id));
      filter();
      map.triggerRepaint();
    },
    setSubmitted(ids, bounds) {
      submitted = [...ids];
      submittedBounds = bounds;
      map.triggerRepaint();
    },
  };
}
